import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import API from "../api/api";
import { BACKEND_URL } from "../api/api";
import toast from "react-hot-toast";

const emptyTrailer = {
  name: "",
  licenseType: "",
  mtm: "",
  emptyWeight: "",
  maxLoadWeight: "",
  length: "",
  width: "",
  height: "",
  depositAmount: "",
  pricePerDay: "",
  pricePerWeek: "",
  pricePerMonth: "",
  photos: "[]",
  options: "[]",
  extraInfo: ""
};

const parseList = (value) => {
  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeUploadResponse = (data) => {
  const values = [
    data?.filePath,
    data?.path,
    ...(Array.isArray(data?.filePaths) ? data.filePaths : []),
    ...(Array.isArray(data?.paths) ? data.paths : []),
    ...(Array.isArray(data?.files) ? data.files : [])
  ];

  return values
    .map((value) => {
      if (!value) return null;
      if (typeof value === "string") return value;
      return value.filePath || value.path || value.url || null;
    })
    .filter(Boolean);
};

const toEditableTrailer = (trailer) => ({
  ...emptyTrailer,
  ...trailer,
  licenseType: trailer.licenseType || "",
  mtm: trailer.mtm ?? "",
  emptyWeight: trailer.emptyWeight ?? "",
  maxLoadWeight: trailer.maxLoadWeight ?? "",
  length: trailer.length ?? "",
  width: trailer.width ?? "",
  height: trailer.height ?? "",
  depositAmount: trailer.depositAmount ?? "",
  pricePerDay: trailer.pricePerDay ?? "",
  pricePerWeek: trailer.pricePerWeek ?? "",
  pricePerMonth: trailer.pricePerMonth ?? "",
  photos: trailer.photos || "[]",
  options: trailer.options || "[]",
  extraInfo: trailer.extraInfo || ""
});

const trailerPayload = (form) => ({
  name: form.name,
  licenseType: form.licenseType,
  mtm: form.mtm,
  emptyWeight: form.emptyWeight,
  maxLoadWeight: form.maxLoadWeight,
  length: form.length,
  width: form.width,
  height: form.height,
  depositAmount: form.depositAmount,
  pricePerDay: form.pricePerDay,
  pricePerWeek: form.pricePerWeek,
  pricePerMonth: form.pricePerMonth,
  photos: JSON.stringify(parseList(form.photos)),
  options: form.options,
  extraInfo: form.extraInfo
});

export default function Trailers() {
  console.log("[trailer-photo] Trailers component loaded");

  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState(emptyTrailer);
  const [newOption, setNewOption] = useState("");

  const { data = [], isLoading } = useQuery({
    queryKey: ["trailers"],
    queryFn: async () => {
      const res = await API.get("/trailers");
      return res.data;
    }
  });

  const selectedTrailer = useMemo(
    () => data.find((trailer) => trailer.id === selectedId),
    [data, selectedId]
  );

  const photos = parseList(form.photos);
  const options = parseList(form.options);

  useEffect(() => {
    if (!data.length) {
      if (!isLoading) {
        setSelectedId(null);
        setForm(emptyTrailer);
      }
      return;
    }

    if (isCreating) {
      return;
    }

    if (!selectedTrailer) {
      setSelectedId(data[0].id);
      return;
    }

    setForm(toEditableTrailer(selectedTrailer));
  }, [data, selectedTrailer, isCreating, isLoading]);

  const refreshTrailers = () => {
    queryClient.invalidateQueries({ queryKey: ["trailers"] });
  };

  const createMutation = useMutation({
    mutationFn: (newTrailer) => API.post("/trailers", newTrailer),
    onSuccess: (res) => {
      refreshTrailers();
      setSelectedId(res.data.id);
      setIsCreating(false);
      toast.success("Trailer aangemaakt");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Trailer aanmaken mislukt");
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: payload }) => API.patch(`/trailers/${id}`, payload),
    onSuccess: () => {
      refreshTrailers();
      toast.success("Trailer opgeslagen");
    },
    onError: (error) => {
      refreshTrailers();
      if (error.response?.status === 404) {
        setSelectedId(null);
        setIsCreating(true);
        setForm(emptyTrailer);
      }
      toast.error(error.response?.data?.error || "Trailer opslaan mislukt");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => API.delete(`/trailers/${id}`),
    onSuccess: () => {
      refreshTrailers();
      toast.success("Verwijderd");
    },
    onError: () => {
      toast.error("Verwijderen mislukt");
    }
  });

  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      console.log("[trailer-photo] upload function entered");
      const selectedFiles = Array.from(files);
      console.log("[trailer-photo] file count", selectedFiles.length);
      const uploadedPaths = [];

      console.log("[trailer-photo] before guard A");
      if (!selectedFiles.length) {
        return uploadedPaths;
      }

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append("image", file);

        console.log("[trailer-photo] before guard B");
        console.log("[trailer-photo] before upload");
        const res = await API.post("/upload", formData);
        uploadedPaths.push(...normalizeUploadResponse(res.data));
      }

      return uploadedPaths;
    },
    onSuccess: (filePaths) => {
      const normalizedPaths = filePaths.filter(Boolean);

      if (!normalizedPaths.length) {
        toast.error("Upload gelukt, maar er werd geen fotopad teruggegeven");
        return;
      }

      setForm((prev) => {
        const nextPhotos = Array.from(new Set([...parseList(prev.photos), ...normalizedPaths]));
        const nextForm = {
          ...prev,
          photos: JSON.stringify(nextPhotos)
        };

        console.log("[trailer-photo] next photos state:", nextPhotos);

        if (!isCreating && selectedId) {
          updateMutation.mutate({ id: selectedId, data: trailerPayload(nextForm) });
        }

        return nextForm;
      });

      toast.success(filePaths.length === 1 ? "Foto toegevoegd" : `${filePaths.length} foto's toegevoegd`);
    },
    onError: () => {
      toast.error("Een of meerdere foto's uploaden is mislukt");
    }
  });

  const updateField = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const saveTrailer = () => {
    const payload = trailerPayload(form);

    if (!form.name.trim()) {
      toast.error("Naam is verplicht");
      return;
    }

    if (form.mtm === "" || form.pricePerDay === "") {
      toast.error("MTM en prijs per dag zijn verplicht");
      return;
    }

    if (isCreating || !selectedId) {
      createMutation.mutate(payload);
      return;
    }

    updateMutation.mutate({
      id: selectedId,
      data: payload
    });
  };

  const createNewTrailer = () => {
    setSelectedId(null);
    setIsCreating(true);
    setForm(emptyTrailer);
  };

  const addOption = () => {
    const value = newOption.trim();
    if (!value) return;

    setForm({
      ...form,
      options: JSON.stringify([...options, value])
    });
    setNewOption("");
  };

  const removeOption = (option) => {
    setForm({
      ...form,
      options: JSON.stringify(options.filter((item) => item !== option))
    });
  };

  const removePhoto = (photo) => {
    const nextForm = {
      ...form,
      photos: JSON.stringify(photos.filter((item) => item !== photo))
    };

    setForm(nextForm);

    if (!isCreating && selectedId) {
      updateMutation.mutate({ id: selectedId, data: trailerPayload(nextForm) });
    }
  };

  if (isLoading) return <div className="loading-state">Trailers laden...</div>;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Trailers</h1>
          <p className="page-subtitle">Beheer technische gegevens, prijzen, foto's, waarborg en vrije opties.</p>
        </div>
        <button className="button secondary" onClick={createNewTrailer}>
          Nieuwe trailer
        </button>
      </header>

      <div className="reservation-workbench">
        <div className="list">
          {data.length === 0 && (
            <div className="panel empty-state">Nog geen trailers.</div>
          )}

          {data.map((trailer) => {
            const thumbnail = parseList(trailer.photos)[0];

            return (
              <button
                key={trailer.id}
                className={`list-row trailer-list-row reservation-row${!isCreating && selectedTrailer?.id === trailer.id ? " selected" : ""}`}
                onClick={() => {
                  setIsCreating(false);
                  setSelectedId(trailer.id);
                }}
              >
                {thumbnail ? (
                  <img className="trailer-list-thumb" src={`${BACKEND_URL}${thumbnail}`} alt={trailer.name} />
                ) : (
                  <div className="trailer-thumb-placeholder">Geen foto</div>
                )}
                <div>
                  <div className="row-title">{trailer.name}</div>
                  <div className="row-meta">
                    MTM {trailer.mtm} kg - EUR {trailer.pricePerDay} per dag - waarborg EUR {trailer.depositAmount || 0}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <aside className="panel panel-pad detail-panel">
          <div className="detail-section">
            <h2 className="page-title" style={{ fontSize: 24 }}>
              {!isCreating && selectedTrailer ? "Trailer bewerken" : "Nieuwe trailer"}
            </h2>
            <p className="page-subtitle">Vul enkel in wat van toepassing is. Opties zijn volledig vrij te kiezen.</p>
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Basisgegevens</h3>
            <div className="form-grid">
              <label className="field"><span className="field-label">Naam</span><input className="input" value={form.name} onChange={(e) => updateField("name", e.target.value)} /></label>
              <label className="field"><span className="field-label">Rijbewijs</span><input className="input" value={form.licenseType} onChange={(e) => updateField("licenseType", e.target.value)} /></label>
              <label className="field"><span className="field-label">MTM trailer</span><input className="input" type="number" value={form.mtm} onChange={(e) => updateField("mtm", e.target.value)} /></label>
              <label className="field"><span className="field-label">Leeggewicht</span><input className="input" type="number" value={form.emptyWeight} onChange={(e) => updateField("emptyWeight", e.target.value)} /></label>
              <label className="field"><span className="field-label">Max gewicht te laden</span><input className="input" type="number" value={form.maxLoadWeight} onChange={(e) => updateField("maxLoadWeight", e.target.value)} /></label>
              <label className="field"><span className="field-label">Waarborg</span><input className="input" type="number" value={form.depositAmount} onChange={(e) => updateField("depositAmount", e.target.value)} /></label>
            </div>
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Afmetingen</h3>
            <div className="form-grid">
              <label className="field"><span className="field-label">Lengte in meter</span><input className="input" type="number" step="0.01" value={form.length} onChange={(e) => updateField("length", e.target.value)} /></label>
              <label className="field"><span className="field-label">Breedte in meter</span><input className="input" type="number" step="0.01" value={form.width} onChange={(e) => updateField("width", e.target.value)} /></label>
              <label className="field"><span className="field-label">Hoogte in meter</span><input className="input" type="number" step="0.01" value={form.height} onChange={(e) => updateField("height", e.target.value)} /></label>
            </div>
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Huurprijzen</h3>
            <div className="form-grid">
              <label className="field"><span className="field-label">Prijs per dag</span><input className="input" type="number" value={form.pricePerDay} onChange={(e) => updateField("pricePerDay", e.target.value)} /></label>
              <label className="field"><span className="field-label">Prijs per week</span><input className="input" type="number" value={form.pricePerWeek} onChange={(e) => updateField("pricePerWeek", e.target.value)} /></label>
              <label className="field"><span className="field-label">Prijs per maand</span><input className="input" type="number" value={form.pricePerMonth} onChange={(e) => updateField("pricePerMonth", e.target.value)} /></label>
            </div>
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Vrije opties</h3>
            <div className="form-grid">
              <input
                className="input"
                placeholder="Bijvoorbeeld oprijrampen, riemen, koppelstuk"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
              />
              <button className="button secondary" onClick={addOption}>Optie toevoegen</button>
            </div>

            <div className="status-line">
              {options.map((option) => (
                <button key={option} className="badge info" onClick={() => removeOption(option)}>
                  {option} x
                </button>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Extra info</h3>
            <textarea
              className="textarea"
              placeholder="Ruimte voor extra informatie, afspraken, aandachtspunten of interne notities."
              value={form.extraInfo}
              onChange={(e) => updateField("extraInfo", e.target.value)}
            />
          </div>

          <div className="detail-section">
            <h3 className="detail-title">Foto's</h3>
            <input
              className="input"
              type="file"
              accept="image/jpeg,image/png"
              multiple
              disabled={uploadMutation.isPending}
              onChange={(e) => {
                console.log("[trailer-photo] file input changed", e.target.files);
                const files = Array.from(e.target.files || []);
                console.log("[trailer-photo] file count", files.length);
                if (files.length) uploadMutation.mutate(files);
                e.target.value = "";
              }}
            />
            {uploadMutation.isPending && (
              <p className="page-subtitle">Foto's uploaden...</p>
            )}

            <div className="photo-grid" style={{ marginTop: 12 }}>
              {photos.map((photo) => (
                <div key={photo} className="photo-thumb-wrap">
                  <img className="photo-thumb" src={`${BACKEND_URL}${photo}`} alt="Trailer" />
                  <button
                    type="button"
                    className="photo-delete-button"
                    onClick={() => {
                      if (window.confirm("Deze foto verwijderen?")) removePhoto(photo);
                    }}
                    aria-label="Foto verwijderen"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <div className="action-row">
              <button className="button" onClick={saveTrailer}>
                {!isCreating && selectedTrailer ? "Opslaan" : "Aanmaken"}
              </button>
              {!isCreating && selectedId && (
                <>
                  <Link className="button secondary" to={`/admin/trailers/${selectedId}/qr`}>
                    QR-code
                  </Link>
                  <button className="button danger" onClick={() => deleteMutation.mutate(selectedId)}>
                    Verwijderen
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
